"use client"

import React, { useEffect, useState } from "react"
import Link from "next/link"
import { Heart } from "lucide-react"
import { useSavedProfiles } from "@/lib/hooks/use-saved-profiles"
import { cn } from "@/lib/utils"

interface SavedProfilesLinkProps {
  className?: string
}

export default function SavedProfilesLink({ className }: SavedProfilesLinkProps) {
  const { getSavedCount } = useSavedProfiles()
  const [count, setCount] = useState(0)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (mounted) {
      // Update count whenever storage changes
      const updateCount = () => setCount(getSavedCount())
      updateCount()

      // Listen for storage events to update across tabs
      const handleStorageChange = () => updateCount()
      window.addEventListener('storage', handleStorageChange)

      // Also update on custom event for same-tab updates
      const handleCustomEvent = () => updateCount()
      window.addEventListener('savedProfilesUpdated', handleCustomEvent)

      return () => {
        window.removeEventListener('storage', handleStorageChange)
        window.removeEventListener('savedProfilesUpdated', handleCustomEvent)
      }
    }
  }, [mounted, getSavedCount])

  // Don't render anything until mounted or if no saved profiles
  if (!mounted || count === 0) {
    return null
  }

  return (
    <Link
      href="/saved-profiles"
      className={cn(
        "flex items-center gap-2 text-white hover:text-[#6b9bd2] transition-colors animate-in fade-in duration-300",
        className
      )}
    >
      <div className="relative">
        <Heart className="h-5 w-5" />
        <span className="absolute -top-2 -right-2 bg-[#6b9bd2] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
          {count > 99 ? '99+' : count}
        </span>
      </div>
      <span className="text-sm font-medium hidden lg:block">Saved</span>
    </Link>
  )
}